import type {
	DTOOf,
	EventEntityType,
	EventType,
	IComment,
	IDataSource,
	IRequest,
	IRequestApproval,
	IRequestApprovalEnriched,
	IRequestInfoGate,
	IRequestStateHistory,
	IRequestType,
	ISystem,
	ITicket,
	StatsMyDTO,
	InfoGateField,
	RequestTypeVersionFullDTO,
	RequestTypeVersionListItemDTO,
} from '@kfolks/types'
import { apiClient } from '@/shared/api/api.client'

export type RequestTypeDTO = DTOOf<IRequestType>
export type RequestDTO = DTOOf<IRequest, 'requestTypeSnapshot'>
export type RequestDetailDTO = DTOOf<IRequest>
export type DataSourceDTO = DTOOf<IDataSource>
export type SystemDTO = DTOOf<ISystem>
export type RequestStateHistoryDTO = DTOOf<IRequestStateHistory>
export type TicketDTO = DTOOf<ITicket>

/**
 * Wire shape of a row from `GET /requests/{id}/info-gates/pending`. The
 * `fieldsSnapshot` is captured server-side at gate-open time so admin
 * edits to the workflow can't reshape what the submitter sees while the
 * gate is open. `parentApprovalId` non-null = side-loop (approver-
 * initiated); null = legacy step-style.
 */
export type RequestInfoGateDTO = Omit<DTOOf<IRequestInfoGate>, 'fieldsSnapshot'> & {
	fieldsSnapshot: InfoGateField[]
}

/**
 * Wire shape of a single row from `GET /requests/{id}/audit` (Spec 3).
 * Mirrors the backend's `AuditTimelineDTO` (entity-owned via
 * `AuditTimelineEntry.toDTO()`); re-declared here so the frontend
 * doesn't pull from the request-center-service workspace.
 *
 * The `data` block is opaque — the renderer per-event-type knows what
 * fields to project.
 */
export interface AuditTimelineEntryDTO {
	eventId: string
	type: EventType
	timestamp: string // ISO 8601
	actorUserId?: string
	actorDisplayName?: string
	originalApproverDisplayName?: string
	data: Record<string, unknown>
}

/**
 * Shape of every `GET /requests/approvals` + `GET /requests/approvals/:id`
 * response row — enriched with parent request + resolved display names by
 * `ApprovalService` (Phase A of the approvals-inbox redesign).
 *
 * `DTOOf` is shallow, so we re-declare the nested `request.submittedAt` as
 * the server-serialized ISO string the backend view emits.
 */
export interface AnsweredInfoGateSummaryDTO {
	id: string
	completedAt: string
	requestedByApproverNote: string | null
	providedDataKeys: string[]
}

export type ApprovalDTO = DTOOf<IRequestApproval> & {
	stepLabel: IRequestApprovalEnriched['stepLabel']
	totalSteps: IRequestApprovalEnriched['totalSteps']
	request: Omit<IRequestApprovalEnriched['request'], 'submittedAt'> & { submittedAt: string }
	approverDisplayNames: IRequestApprovalEnriched['approverDisplayNames']
	approverGroupNames: IRequestApprovalEnriched['approverGroupNames']
	resolvedManagerDisplayName: IRequestApprovalEnriched['resolvedManagerDisplayName']
	viewerMatch: IRequestApprovalEnriched['viewerMatch']
	answeredInfoGate: AnsweredInfoGateSummaryDTO | null
}

/**
 * Response shape for `GET /requests/stats/my` — re-exported from the canonical
 * `StatsMyDTO` in `@kfolks/types` (Spec 4 expanded the shape to include
 * `pendingExecution` + `hadEverPendingApproval`; the previous frontend-local
 * `MyStatsDTO` was a stale 2-field shadow that pre-dated that work).
 */
export type MyStatsDTO = StatsMyDTO

/**
 * Wire shape returned by `POST /requests/types/:id/{form,workflow}/validate`
 * (Spec 5). Mirrors the backend's `ValidationResult` from `@kfolks/models`
 * (`form-schema-validator.ts`) — re-declared here so the FE doesn't reach
 * into deep `@kfolks/models` paths.
 */
export interface RequestCenterValidationError {
	path: string
	message: string
	severity: 'error' | 'warning'
}

export interface RequestCenterValidationResult {
	valid: boolean
	errors: RequestCenterValidationError[]
}

/**
 * Unified shape for every data-source search result. Backend guarantees
 * `value`, `label`, and `name` are always present. `meta` carries
 * source-specific structured extras (e.g. `meta.source = 'client' |
 * 'contractor'` on partners.all).
 */
export interface DataSourceSearchItem {
	value: string
	label: string
	name: string
	meta?: Record<string, string>
}

export interface PageEnvelope<T> {
	data: T[]
	total: number
}

export interface ListRequestTypeParams {
	category?: string
	includeInactive?: boolean
	limit?: number
	offset?: number
}

export interface ListRequestParams {
	state?: string
	type?: string
	priority?: string
	submitter?: string
	assignedToMe?: boolean
	from?: string
	to?: string
	/** Restrict to rows whose `metadata.productiveError` is set (admin operational view). */
	productiveErrorsOnly?: boolean
	limit?: number
	offset?: number
}

export interface ListApprovalParams {
	type?: string
	approverId?: string
	/**
	 * Caller-scope shorthand used by the dashboard's Pending Approvals
	 * widget. Equivalent to omitting `scope` (default scope is `mine`)
	 * — emits the literal `filters[assignedToMe]=true` URL the spec
	 * documents in `request-center-dashboard.md`. Backend treats both
	 * forms as the same filter.
	 */
	assignedToMe?: boolean
	/** Admin-only: `mine` (default) or `all`. Silently clamped to `mine` for non-admins by the backend. */
	scope?: 'mine' | 'all'
	/** Comma-separated priority list, e.g. `"high,critical"`. */
	priority?: string
	/** ISO date-time. Matches approvals whose `dueAt` is strictly before this. */
	dueBefore?: string
	/** ISO date-time. Matches approvals whose `dueAt` is strictly after this. */
	dueAfter?: string
	/** Submitter user UUID. */
	submitter?: string
	/** ILIKE match on `requests.request_number`. */
	search?: string
	/** `'pending'` (default) or `'any'`. */
	status?: 'pending' | 'any'
	sort?: 'priority' | 'due' | 'age' | 'type'
	order?: 'asc' | 'desc'
	limit?: number
	offset?: number
}

const filterParam = (key: string, value: string | number | boolean | undefined): [string, string][] =>
	value === undefined || value === '' ? [] : [[`filters[${key}]`, String(value)]]

const buildListTypeQuery = (p: ListRequestTypeParams): URLSearchParams => {
	const entries: [string, string][] = [
		...filterParam('category', p.category),
		...filterParam('includeInactive', p.includeInactive),
	]
	if (p.limit !== undefined) entries.push(['limit', String(p.limit)])
	if (p.offset !== undefined) entries.push(['offset', String(p.offset)])
	return new URLSearchParams(entries)
}

const buildListRequestQuery = (p: ListRequestParams): URLSearchParams => {
	const entries: [string, string][] = [
		...filterParam('state', p.state),
		...filterParam('type', p.type),
		...filterParam('priority', p.priority),
		...filterParam('submitter', p.submitter),
		...filterParam('from', p.from),
		...filterParam('to', p.to),
	]
	if (p.assignedToMe) entries.push(['filters[assignedToMe]', 'true'])
	if (p.productiveErrorsOnly) entries.push(['filters[productiveErrorsOnly]', 'true'])
	if (p.limit !== undefined) entries.push(['limit', String(p.limit)])
	if (p.offset !== undefined) entries.push(['offset', String(p.offset)])
	return new URLSearchParams(entries)
}

const buildListApprovalQuery = (p: ListApprovalParams): URLSearchParams => {
	const entries: [string, string][] = [
		...filterParam('type', p.type),
		...filterParam('approverId', p.approverId),
		...filterParam('scope', p.scope),
		...filterParam('priority', p.priority),
		...filterParam('dueBefore', p.dueBefore),
		...filterParam('dueAfter', p.dueAfter),
		...filterParam('submitter', p.submitter),
		...filterParam('search', p.search),
		...filterParam('status', p.status),
	]
	if (p.assignedToMe) entries.push(['filters[assignedToMe]', 'true'])
	if (p.sort) entries.push(['sort', p.sort])
	if (p.order) entries.push(['order', p.order])
	if (p.limit !== undefined) entries.push(['limit', String(p.limit)])
	if (p.offset !== undefined) entries.push(['offset', String(p.offset)])
	return new URLSearchParams(entries)
}

export const requestCenterApi = {
	// ─── Catalog ─────────────────────────────────────────────────────────────
	listRequestTypes: async (params: ListRequestTypeParams = {}): Promise<RequestTypeDTO[]> => {
		const res = await apiClient.get<RequestTypeDTO[]>(`/requests/types?${buildListTypeQuery(params)}`)
		return res.data
	},

	getRequestTypeBySlug: async (slug: string): Promise<RequestTypeDTO> => {
		const res = await apiClient.get<RequestTypeDTO>(`/requests/types/${encodeURIComponent(slug)}`)
		return res.data
	},

	createRequestType: async (input: Partial<RequestTypeDTO>): Promise<RequestTypeDTO> => {
		const res = await apiClient.post<RequestTypeDTO>('/requests/types', input)
		return res.data
	},

	updateRequestType: async (id: string, input: Partial<RequestTypeDTO>): Promise<RequestTypeDTO> => {
		const res = await apiClient.put<RequestTypeDTO>(`/requests/types/${id}`, input)
		return res.data
	},

	deleteRequestType: async (id: string): Promise<void> => {
		await apiClient.delete<void>(`/requests/types/${id}`)
	},

	// ─── Visual builder: validate + versioning (Spec 5) ──────────────────────

	/**
	 * Validate a `formSchema` payload without saving. Returns the same
	 * `{ valid, errors }` shape `RequestTypeService.create / .update`
	 * enforces on save. Always 200 — even malformed payloads come back as
	 * `{ valid: false, errors: [...] }` so the visual builder can render
	 * inline authoring errors. See ADR-0003 / Spec 5 §0.
	 */
	validateFormSchemaForType: async (typeId: string, formSchema: unknown): Promise<RequestCenterValidationResult> => {
		const res = await apiClient.post<RequestCenterValidationResult>(`/requests/types/${typeId}/form/validate`, {
			formSchema,
		})
		return res.data
	},

	/**
	 * Validate a `workflowConfig` payload without saving. Same shape as
	 * `validateFormSchemaForType`; uses the linear step-array contract per
	 * ADR-0012 (reachability via `after.next`, terminal-resolution,
	 * approver-dimension presence). Always 200.
	 */
	validateWorkflowConfigForType: async (
		typeId: string,
		workflowConfig: unknown,
	): Promise<RequestCenterValidationResult> => {
		const res = await apiClient.post<RequestCenterValidationResult>(`/requests/types/${typeId}/workflow/validate`, {
			workflowConfig,
		})
		return res.data
	},

	/**
	 * List authoring history for a request type, newest-first. Each row is
	 * metadata-only — `formSchema` and `workflowConfig` are NOT in this
	 * payload. Fetch a single full version via `getRequestTypeVersion` for
	 * preview / diff / restore-confirmation flows.
	 */
	listRequestTypeVersions: async (
		typeId: string,
		params: { page?: number; pageSize?: number } = {},
	): Promise<RequestTypeVersionListItemDTO[]> => {
		const entries: [string, string][] = []
		if (params.page !== undefined) entries.push(['page', String(params.page)])
		if (params.pageSize !== undefined) entries.push(['pageSize', String(params.pageSize)])
		const qs = entries.length > 0 ? `?${new URLSearchParams(entries).toString()}` : ''
		const res = await apiClient.get<RequestTypeVersionListItemDTO[]>(`/requests/types/${typeId}/versions${qs}`)
		return res.data
	},

	/**
	 * Fetch a single version with its full `formSchema` + `workflowConfig` +
	 * `summaryTemplate` payload. Used by the History panel's preview, the
	 * full-diff modal (alongside its v(N-1) sibling), and the restore
	 * confirmation view.
	 */
	getRequestTypeVersion: async (typeId: string, versionId: string): Promise<RequestTypeVersionFullDTO> => {
		const res = await apiClient.get<RequestTypeVersionFullDTO>(`/requests/types/${typeId}/versions/${versionId}`)
		return res.data
	},

	/**
	 * Restore a prior version. Backend re-validates the payload against the
	 * current world (data sources, summary-template field ids, workflow
	 * graph) and fails closed with a `ValidationError → 422` mapping if the
	 * payload is stale. On success, `request_types` is updated AND a NEW
	 * `request_type_versions` row is appended with `change_note = "Restored
	 * from v{N}: <note>"`. Historical rows never mutate.
	 */
	restoreRequestTypeVersion: async (
		typeId: string,
		versionId: string,
		input: { changeNote?: string | null } = {},
	): Promise<RequestTypeDTO> => {
		const res = await apiClient.post<RequestTypeDTO>(`/requests/types/${typeId}/versions/${versionId}/restore`, {
			changeNote: input.changeNote ?? null,
		})
		return res.data
	},

	// ─── Submission ──────────────────────────────────────────────────────────
	submitRequest: async (input: {
		requestTypeId: string
		formData: Record<string, unknown>
		/**
		 * Submit-time labels for any data-source / select-style field whose
		 * value is an opaque id (e.g. provider UUID → "GitHub"). Persisted
		 * onto `formData.__labels` so the row summary on /my, dashboard
		 * widgets, and the digest stays audit-stable when a provider is
		 * renamed downstream. Optional — backend resolves static-options
		 * labels itself; only autocomplete/data-source fields need this.
		 */
		formDataLabels?: Record<string, string>
		priority?: string
	}): Promise<RequestDTO> => {
		const res = await apiClient.post<RequestDTO>('/requests', input)
		return res.data
	},

	listRequests: async (params: ListRequestParams = {}): Promise<PageEnvelope<RequestDTO>> => {
		const res = await apiClient.get<PageEnvelope<RequestDTO>>(`/requests?${buildListRequestQuery(params)}`)
		return res.data
	},

	getRequest: async (id: string): Promise<RequestDetailDTO> => {
		const res = await apiClient.get<RequestDetailDTO>(`/requests/${id}`)
		return res.data
	},

	/**
	 * Append-only state-history audit trail for a request — one row per
	 * `current_state` transition, oldest → newest. Submitter + admin only
	 * (401/404 surface otherwise).
	 */
	listRequestStateHistory: async (id: string): Promise<{ data: RequestStateHistoryDTO[] }> => {
		const res = await apiClient.get<{ data: RequestStateHistoryDTO[] }>(`/requests/${id}/history`)
		return res.data
	},

	/**
	 * Spec 3 audit timeline — lifecycle + approval + comment + ticket events
	 * for one request, ordered oldest → newest. Comment events are filtered
	 * server-side by the comments-visibility predicate, so the response the
	 * caller sees already matches what they're authorised to see.
	 *
	 * Replaces `listRequestStateHistory` in the UI (the older endpoint
	 * still works but emits `Deprecation: true`).
	 */
	listAudit: async (id: string): Promise<{ data: AuditTimelineEntryDTO[] }> => {
		const res = await apiClient.get<{ data: AuditTimelineEntryDTO[] }>(`/requests/${id}/audit`)
		return res.data
	},

	/**
	 * Tickets linked to a request — drives `<TicketsSection requestId />`
	 * on `RequestDetailPage`. Same auth scope as `getRequest`. Empty array
	 * is a valid response for a request with no linked tickets.
	 */
	listTicketsByRequest: async (id: string): Promise<{ data: TicketDTO[] }> => {
		const res = await apiClient.get<{ data: TicketDTO[] }>(`/requests/${id}/tickets`)
		return res.data
	},

	/**
	 * Admin cross-request audit search (Spec 3, Group 11.2). Filters:
	 *   - `actor` — userId of the actor who emitted the event
	 *   - `actionType` — comma-separated event-type list
	 *   - `entityType` — parent entity kind (defaults to `request_center_request`)
	 *   - `from` / `to` — ISO 8601 timestamp bounds
	 * Pagination via `limit` (max 500, default 50) + `page`.
	 */
	searchAudit: async (
		params: {
			actor?: string
			actionType?: EventType[] | string[]
			entityType?: EventEntityType
			from?: string
			to?: string
			limit?: number
			page?: number
		} = {},
	): Promise<{ data: AuditTimelineEntryDTO[]; total: number }> => {
		const qs = new URLSearchParams()
		if (params.actor) qs.set('filters[actor]', params.actor)
		if (params.actionType && params.actionType.length > 0) qs.set('filters[actionType]', params.actionType.join(','))
		if (params.entityType) qs.set('filters[entityType]', params.entityType)
		if (params.from) qs.set('filters[from]', params.from)
		if (params.to) qs.set('filters[to]', params.to)
		if (params.limit) qs.set('limit', String(params.limit))
		if (params.page) qs.set('page', String(params.page))
		const res = await apiClient.get<{ data: AuditTimelineEntryDTO[]; total: number }>(`/requests/audit?${qs}`)
		return res.data
	},

	// ─── Data sources ────────────────────────────────────────────────────────
	listDataSources: async (): Promise<DataSourceDTO[]> => {
		const res = await apiClient.get<DataSourceDTO[]>('/requests/data-sources')
		return res.data
	},

	searchDataSource: async (
		key: string,
		query: { q?: string; limit?: number } = {},
	): Promise<{ data: DataSourceSearchItem[] }> => {
		const params = new URLSearchParams()
		if (query.q !== undefined && query.q !== '') params.set('q', query.q)
		if (query.limit !== undefined) params.set('limit', String(query.limit))
		const res = await apiClient.get<{ data: DataSourceSearchItem[] }>(
			`/requests/data-sources/${encodeURIComponent(key)}/search?${params}`,
		)
		return res.data
	},

	createDataSource: async (input: Partial<DataSourceDTO>): Promise<DataSourceDTO> => {
		const res = await apiClient.post<DataSourceDTO>('/requests/data-sources', input)
		return res.data
	},

	updateDataSource: async (id: string, input: Partial<DataSourceDTO>): Promise<DataSourceDTO> => {
		const res = await apiClient.put<DataSourceDTO>(`/requests/data-sources/${id}`, input)
		return res.data
	},

	// ─── Systems ─────────────────────────────────────────────────────────────
	listSystems: async (): Promise<SystemDTO[]> => {
		const res = await apiClient.get<SystemDTO[]>('/requests/systems')
		return res.data
	},

	createSystem: async (input: Partial<SystemDTO>): Promise<SystemDTO> => {
		const res = await apiClient.post<SystemDTO>('/requests/systems', input)
		return res.data
	},

	updateSystem: async (id: string, input: Partial<SystemDTO>): Promise<SystemDTO> => {
		const res = await apiClient.put<SystemDTO>(`/requests/systems/${id}`, input)
		return res.data
	},

	deleteSystem: async (id: string): Promise<void> => {
		await apiClient.delete<void>(`/requests/systems/${id}`)
	},

	// ─── Lifecycle actions ───────────────────────────────────────────────────

	cancelRequest: async (id: string, reason?: string): Promise<RequestDetailDTO> => {
		const res = await apiClient.put<RequestDetailDTO>(`/requests/${id}/cancel`, { reason })
		return res.data
	},

	answerInfoGate: async (
		requestId: string,
		stepId: string,
		providedData: Record<string, unknown>,
	): Promise<RequestDetailDTO> => {
		const res = await apiClient.post<RequestDetailDTO>(
			`/requests/${requestId}/info-gate/${encodeURIComponent(stepId)}`,
			{ providedData },
		)
		return res.data
	},

	listPendingInfoGates: async (requestId: string): Promise<RequestInfoGateDTO[]> => {
		const res = await apiClient.get<{ data: RequestInfoGateDTO[] }>(`/requests/${requestId}/info-gates/pending`)
		return res.data.data
	},

	myStats: async (): Promise<MyStatsDTO> => {
		const res = await apiClient.get<MyStatsDTO>('/requests/stats/my')
		return res.data
	},

	// ─── Approvals inbox ─────────────────────────────────────────────────────

	listApprovals: async (params: ListApprovalParams = {}): Promise<PageEnvelope<ApprovalDTO>> => {
		const res = await apiClient.get<PageEnvelope<ApprovalDTO>>(`/requests/approvals?${buildListApprovalQuery(params)}`)
		return res.data
	},

	getApproval: async (id: string): Promise<ApprovalDTO> => {
		const res = await apiClient.get<ApprovalDTO>(`/requests/approvals/${id}`)
		return res.data
	},

	/**
	 * Most-recent **answered** side-loop info-gate for the given approval, with
	 * full `providedData` + `fieldsSnapshot` so the approval-detail page can
	 * render the answered field labels + values inline. Returns `null` when
	 * the approver never asked for more info on this approval (or the open
	 * gate hasn't been answered yet).
	 */
	getAnsweredInfoGate: async (approvalId: string): Promise<RequestInfoGateDTO | null> => {
		const res = await apiClient.get<RequestInfoGateDTO | null>(`/requests/approvals/${approvalId}/answered-info-gate`)
		return res.data
	},

	/**
	 * Full approval timeline for a request — every row (pending + decided)
	 * ordered by `stepOrder`. Backs the detail-page timeline.
	 */
	listApprovalsByRequest: async (requestId: string): Promise<{ data: ApprovalDTO[] }> => {
		const res = await apiClient.get<{ data: ApprovalDTO[] }>(`/requests/${requestId}/approvals`)
		return res.data
	},

	approveApproval: async (id: string, comment?: string): Promise<ApprovalDTO> => {
		const res = await apiClient.post<ApprovalDTO>(`/requests/approvals/${id}/approve`, { comment })
		return res.data
	},

	rejectApproval: async (id: string, comment: string): Promise<ApprovalDTO> => {
		const res = await apiClient.post<ApprovalDTO>(`/requests/approvals/${id}/reject`, { comment })
		return res.data
	},

	/**
	 * Approver pauses an approval to ask the submitter for more info.
	 * Server inserts a side-loop `request_info_gates` row + pauses SLA.
	 * Returns the updated approval (now with `slaPausedAt`) and the freshly
	 * created info-gate row so the UI can immediately show the pending state.
	 */
	requestInfoOnApproval: async (
		approvalId: string,
		payload: { fields: InfoGateField[]; note?: string; dueAt?: string },
	): Promise<{ approval: ApprovalDTO; infoGate: RequestInfoGateDTO }> => {
		const res = await apiClient.post<{ approval: ApprovalDTO; infoGate: RequestInfoGateDTO }>(
			`/requests/approvals/${approvalId}/request-info`,
			payload,
		)
		return res.data
	},

	/**
	 * Withdraw an open side-loop info-request without the submitter answering.
	 * Approver-or-admin only. Returns the cancelled gate + the resumed approval.
	 */
	cancelInfoRequest: async (infoGateId: string): Promise<{ approval: ApprovalDTO; infoGate: RequestInfoGateDTO }> => {
		const res = await apiClient.post<{ approval: ApprovalDTO; infoGate: RequestInfoGateDTO }>(
			`/requests/info-gates/${infoGateId}/cancel`,
		)
		return res.data
	},
}

// ─── Comments ───────────────────────────────────────────────────────────────
//
// Parent-scoped comments API for Request Center (per ADR-0011).
//
// The browser ALWAYS goes through `/requests/{requestId}/comments/*`
// — never `/comments/*` directly (those are admin-only by design).
// Future parent types (incidents, projects, etc.) ship their own
// api file with the same `CommentApiClient` shape so `<CommentsPanel>`
// stays parent-agnostic.

/** Enriched comment row returned by `GET /requests/:id/comments` —
 *  the canonical Comment DTO plus the resolved author display name (so
 *  the frontend doesn't have to fan out an admin-only `/users/{id}`
 *  lookup per row). `authorDisplayName` is null when user-service can't
 *  resolve the userId (deleted user, system actor without a user row). */
export type CommentDTO = DTOOf<IComment> & { authorDisplayName?: string | null }

export interface CommentMetadata {
	mentions?: string[]
}

export interface CommentCreateBody {
	content: string
	isInternal?: boolean
	metadata?: CommentMetadata
}

export interface CommentUpdateBody {
	content?: string
	isInternal?: boolean
	metadata?: CommentMetadata
}

/**
 * Shape `<CommentsPanel>` consumes — every parent-type embed wires its
 * own implementation. Each method takes the parent id (`requestId` for
 * the Request Center surface) so the client can build the path.
 */
export interface CommentApiClient {
	list(parentId: string): Promise<{ data: CommentDTO[] }>
	create(parentId: string, body: CommentCreateBody): Promise<CommentDTO>
	update(parentId: string, commentId: string, body: CommentUpdateBody): Promise<CommentDTO>
	softDelete(parentId: string, commentId: string): Promise<void>
}

export const requestCommentApi: CommentApiClient = {
	async list(requestId) {
		const res = await apiClient.get<{ data: CommentDTO[] }>(`/requests/${requestId}/comments`)
		return res.data
	},
	async create(requestId, body) {
		const res = await apiClient.post<CommentDTO>(`/requests/${requestId}/comments`, body)
		return res.data
	},
	async update(requestId, commentId, body) {
		const res = await apiClient.put<CommentDTO>(`/requests/${requestId}/comments/${commentId}`, body)
		return res.data
	},
	async softDelete(requestId, commentId) {
		await apiClient.delete(`/requests/${requestId}/comments/${commentId}`)
	},
}
